/**
 * Firm usage quota (DOP-280).
 *
 * The pool is the FIRM's, not the caller's: every deployment is single-tenant,
 * so this one number covers everyone signed in to it.
 *
 * The schema is deliberately two shapes in one. A deployment with no contracted
 * pool answers `{ enabled: false }` and nothing else — not a zeroed pool — so the
 * numeric fields are optional and the page has to branch on `enabled` rather than
 * render whatever arrived.
 */

import { z } from "zod/v3";
import { apiFetch } from "../utils/apiClient";

const usageQuotaSchema = z.object({
	enabled: z.boolean(),
	/** Epoch milliseconds, half-open: `[period_start, period_end)`. */
	period_start: z.number().nullable().optional(),
	period_end: z.number().nullable().optional(),
	pool_tokens: z.number().nullable().optional(),
	used_tokens: z.number().nullable().optional(),
	remaining_tokens: z.number().nullable().optional(),
	/** UNCAPPED — 1.4 means the firm is 40% past its pool. */
	pct_used: z.number().nullable().optional(),
	/** Tokens per displayed CREDIT (contract term; the UI divides by it). */
	tokens_per_credit: z.number().nullable().optional(),
	/**
	 * The part of `used_tokens` that is implementation work Doppler's engineers
	 * did for the firm (DOP-561). Absent on a backend that predates it.
	 */
	implementation_tokens: z.number().nullable().optional(),
});

export type UsageQuota = z.infer<typeof usageQuotaSchema>;

export const getUsageQuota = async (): Promise<UsageQuota> => {
	const response = await apiFetch("/api/v3/usage/quota", { method: "GET" });
	if (!response.ok) {
		throw new Error("Could not load usage.");
	}
	return usageQuotaSchema.parse(await response.json());
};
